import { useSubscription } from "@selector/shared";
import { type FC, memo, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { PubSubActions } from "@/global/pubsub-actions.js";
import type { Organism, SplitPaneLayoutState, TableQuerySpec } from "@/graphql.ts";
import {
  createTableSpecForOrganismParent,
  initDataTables,
  onRestrictionChange,
  sameTableSpec,
  triggerTableRefresh,
} from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { ENTITY_TYPE, TableRestriction } from "@/settings/settings.ts";
import { useGenomicTableCollections } from "@/ui-modules/genome-viewer/hooks/useGenomicTableCollections.ts";

import style from "../styles/GenomicFeatureTable.module.scss";
import { FeaturesTablePane, FeaturesTableToolbar, type ITabbedTableCollection } from "./GenomeViewerTabbedTableView.js";

// special case for the listing of mutant genomes
export const MUTANT_GENOMES_COLLECTION_NAME = "Variant Genomes";

interface GenomicFeatureTableProps {
  openedFromUrlParam?: boolean;
}

/**
 * Side table next to GenomeBrowser.
 *
 * created on 12/14/2016, rebuild as FC on 22/04/2022
 */

const GenomicFeatureTable: FC<GenomicFeatureTableProps> = memo(function GenomicFeatureTable(props) {
  const dispatch = useDispatch<ReduxDispatch>();
  const genome = useSelector<IAppState, Organism>((state) => state.genomeBrowser.genome);
  const genomeIsLoading = useSelector<IAppState, boolean>((state) => state.genomeBrowser.isLoading);
  const featuresTableLayout = useSelector<IAppState, SplitPaneLayoutState>(
    (state) => state.genomeBrowser.featuresTableLayout,
  );
  const activeFeaturesTable = useSelector<IAppState, TableQuerySpec>(
    (state) => state.genomeBrowser.activeFeaturesTable,
  );

  useSubscription(PubSubActions.REFETCH_REGION_FLAGS, () => {
    dispatch(triggerTableRefresh(createTableSpecForOrganismParent(ENTITY_TYPE.REGION_FLAG, genome.id)));
  });

  // once the FeatureTable has been shown once we keep the component "alive" which means that all internal states are preserved (scrolling state, active tab...)
  const [keepAlive, setKeepAlive] = useState<boolean>(!featuresTableLayout.hidden);

  // once the table panel has been open we keep it alive, preserving all local states
  useEffect(() => {
    if (!featuresTableLayout.hidden) {
      setKeepAlive(true);
    }
  }, [featuresTableLayout.hidden]);

  const collections = useGenomicTableCollections();

  // initialize the columns of all the tables so that the filtering storage can be used and synced
  useEffect(() => {
    dispatch(initDataTables(collections.map((collection) => collection.tableSpec)));
  }, [dispatch, collections]);

  // find the collection corresponding to the active table (set either manually using selectActiveFeaturesTable or automatically in setTrackList function)
  const activeCollectionItem = useMemo(() => {
    return collections?.find((collection: ITabbedTableCollection) =>
      sameTableSpec(collection.tableSpec, activeFeaturesTable),
    );
  }, [collections, activeFeaturesTable]);

  useEffect(() => {
    if (props.openedFromUrlParam && activeFeaturesTable.resultType === ENTITY_TYPE.BED_FEATURE) {
      // If BED table is shown using url search params set the visible range to entire genome, see SEL10-624
      dispatch(onRestrictionChange(TableRestriction.GENOME));
    }
  }, []);

  return (
    <div className={style.genomic_feature_table}>
      <FeaturesTableToolbar
        collections={collections}
        selectedCollection={activeCollectionItem}
        verticalLayout={featuresTableLayout.verticalLayout}
      />
      <FeaturesTablePane
        collections={collections}
        isLoading={!keepAlive || genomeIsLoading}
        selectedCollection={activeCollectionItem}
      />
    </div>
  );
});

export default GenomicFeatureTable;
